import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { industryService } from '../utils/apiService';
import { validateIndustryData } from '../utils/safeAccess';
import logger from '../utils/logger';

// Initial state
const initialState = {
  // Industries data
  industries: [],
  selectedIndustry: 'Food & Beverage',
  industriesLoading: false,
  industriesError: null,
  
  // App settings
  region: 'naics',
  
  // UI state
  showAssessmentsDropdown: false,
  
  // Loading states
  globalLoading: false,
  
  // Error state
  globalError: null,
  
  // Admin settings
  isAdmin: true  // For now, set to true for admin functionality
};

// Action types
const ActionTypes = {
  SET_INDUSTRIES: 'SET_INDUSTRIES',
  SET_INDUSTRIES_LOADING: 'SET_INDUSTRIES_LOADING',
  SET_INDUSTRIES_ERROR: 'SET_INDUSTRIES_ERROR',
  SET_SELECTED_INDUSTRY: 'SET_SELECTED_INDUSTRY',
  SET_REGION: 'SET_REGION',
  SET_ASSESSMENTS_DROPDOWN: 'SET_ASSESSMENTS_DROPDOWN',
  SET_GLOBAL_LOADING: 'SET_GLOBAL_LOADING',
  SET_GLOBAL_ERROR: 'SET_GLOBAL_ERROR',
  RESET_STATE: 'RESET_STATE'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_INDUSTRIES:
      return {
        ...state,
        industries: action.payload.map(validateIndustryData),
        industriesLoading: false,
        industriesError: null
      };
      
    case ActionTypes.SET_INDUSTRIES_LOADING:
      return {
        ...state,
        industriesLoading: action.payload
      };
      
    case ActionTypes.SET_INDUSTRIES_ERROR:
      return {
        ...state,
        industriesError: action.payload,
        industriesLoading: false
      };
      
    case ActionTypes.SET_SELECTED_INDUSTRY:
      return {
        ...state,
        selectedIndustry: action.payload
      };
      
    case ActionTypes.SET_REGION:
      return {
        ...state,
        region: action.payload
      };
      
    case ActionTypes.SET_ASSESSMENTS_DROPDOWN:
      return {
        ...state,
        showAssessmentsDropdown: action.payload
      };
      
    case ActionTypes.SET_GLOBAL_LOADING:
      return {
        ...state,
        globalLoading: action.payload
      };
      
    case ActionTypes.SET_GLOBAL_ERROR:
      return {
        ...state,
        globalError: action.payload
      };
      
    case ActionTypes.RESET_STATE:
      return initialState;
      
    default:
      return state;
  }
};

// Context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Actions
  const actions = {
    // Load industries
    async loadIndustries() {
      dispatch({ type: ActionTypes.SET_INDUSTRIES_LOADING, payload: true });
      
      const result = await industryService.getAll();
      
      if (result.success) {
        dispatch({ type: ActionTypes.SET_INDUSTRIES, payload: result.data });
        
        // Set default industry if none selected and industries available
        if (!state.selectedIndustry && result.data.length > 0) {
          dispatch({ type: ActionTypes.SET_SELECTED_INDUSTRY, payload: result.data[0].name });
        }
      } else {
        // Handle error gracefully
        // Don't log 401 errors as errors - they're expected for unauthenticated users
        if (result.isUnauthenticated) {
          // Silently handle - industries endpoint may require auth
          // Landing page can still function without industries data
          // Set empty industries array and no error state
          dispatch({ type: ActionTypes.SET_INDUSTRIES, payload: [] });
          dispatch({ type: ActionTypes.SET_INDUSTRIES_ERROR, payload: null });
          logger.debug('Industries endpoint requires authentication (expected for unauthenticated users)');
        } else {
          // Only log unexpected errors
          dispatch({ type: ActionTypes.SET_INDUSTRIES_ERROR, payload: result.error });
          if (!result.isOffline && result.error) {
            logger.error('Failed to load industries:', result.error);
          }
        }
      }
    },

    // Set selected industry
    setSelectedIndustry(industry) {
      dispatch({ type: ActionTypes.SET_SELECTED_INDUSTRY, payload: industry });
    },

    // Set region
    setRegion(region) {
      dispatch({ type: ActionTypes.SET_REGION, payload: region });
    },

    // Toggle assessments dropdown
    toggleAssessmentsDropdown() {
      dispatch({ 
        type: ActionTypes.SET_ASSESSMENTS_DROPDOWN, 
        payload: !state.showAssessmentsDropdown 
      });
    },

    // Close assessments dropdown
    closeAssessmentsDropdown() {
      dispatch({ 
        type: ActionTypes.SET_ASSESSMENTS_DROPDOWN, 
        payload: false 
      });
    },

    // Set global loading
    setGlobalLoading(loading) {
      dispatch({ type: ActionTypes.SET_GLOBAL_LOADING, payload: loading });
    },

    // Set global error
    setGlobalError(error) {
      dispatch({ type: ActionTypes.SET_GLOBAL_ERROR, payload: error });
    },

    // Clear global error
    clearGlobalError() {
      dispatch({ type: ActionTypes.SET_GLOBAL_ERROR, payload: null });
    },

    // Reset state
    resetState() {
      dispatch({ type: ActionTypes.RESET_STATE });
    }
  };

  // Load industries on mount
  useEffect(() => {
    actions.loadIndustries();
  }, []);

  const value = {
    state,
    actions,
    // Computed values
    isLoading: state.industriesLoading || state.globalLoading,
    hasError: state.industriesError || state.globalError,
    availableIndustries: state.industries,
    currentIndustry: state.industries.find(ind => ind.name === state.selectedIndustry),
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;